import {
  CA_CERTIFICATE_FIELDS,
  INVALID_CA_CERTIFICATE_MESSAGE,
} from "../../../shared/connectors/caCertificate";
import { validCaCertificateUpload } from "./validateCaCertificate";
import { useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { useEffect, useEffectEvent, useRef, useState } from "react";
import type { FileField } from "../../../shared/connectors/types";
import { sharedMutators } from "../../../shared/mutators";
import {
  queueConnectorEdit,
  withConnectorDestinationProof,
} from "../../sealSecret";
import { sealSecretInput } from "./sealSecretInput";
import { emitErrorToast } from "../../lib/errorToast";
import { Box } from "../Box";
import { ButtonPress } from "../Button/ButtonPress";
import { Icon } from "../Icon";
import { PressButton } from "../PressButton";
import { Text } from "../Text";
import type { App } from "./Form";
import { useConfirmGrantRemoval } from "./useConfirmGrantRemoval";

const MAX_FILE_BYTES = 1024 * 1024;

const UploadButton = styled(PressButton)`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background-color: var(--surface-primary);
  color: var(--text-primary);
  outline: 0.5px solid var(--border-alpha);
  border-radius: 8px;
  border: 0;
  font-size: 12px;
  font-family: InterVariable;
  line-height: 16px;
  height: 32px;
  cursor: pointer;
  transition: box-shadow var(--motion-fast) var(--ease-out);
  &:hover {
    background: var(--alpha4);
  }
`;

const SavedRow = styled("div")`
  display: flex;
  align-items: center;
  gap: 8px;
`;

type Props = {
  item: FileField;
  app: App;
  disabled?: boolean;
  onEdit?: () => void;
  saveError?: boolean;
  onSaveStatus?: (saved: boolean) => void;
};

export function FileFieldItem({
  item,
  app,
  disabled,
  onEdit,
  saveError,
  onSaveStatus,
}: Props) {
  const z = useZero();
  const { confirmGrantRemoval, confirmModal } = useConfirmGrantRemoval(app);
  const inputRef = useRef<HTMLInputElement>(null);
  const uploadVersion = useRef(0);
  const mounted = useRef(false);
  const pendingRead = useRef<PromiseWithResolvers<void> | null>(null);
  const onUnfinishedUpload = useEffectEvent(() => onSaveStatus?.(false));
  useEffect(() => {
    const active = mounted;
    const version = uploadVersion;
    const read = pendingRead;
    active.current = true;
    return () => {
      active.current = false;
      version.current++;
      if (read.current) {
        read.current.resolve();
        read.current = null;
        onUnfinishedUpload();
      }
    };
  }, []);
  const [fileName, setFileName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { target } = item;

  // `<input type="file">` renders empty after save, so presence companions
  // let the UI show an existing secret without replicating its value.
  const hasSaved =
    target.kind === "configuration"
      ? (app.configuration?.[target.field]?.length ?? 0) > 0
      : target.kind === "secrets"
        ? (app.secretKeys?.includes(target.field) ?? false)
        : target.kind === "oauthConnection" && target.field === "clientSecret"
          ? (app.hasOAuthClientSecret ?? false)
          : false;

  const handleFile = (file: File) => {
    const input = inputRef.current;
    if (!input) return;
    const version = ++uploadVersion.current;
    onEdit?.();
    pendingRead.current?.resolve();
    pendingRead.current = null;
    onSaveStatus?.(true);
    const finishRead = () => {
      pendingRead.current?.resolve();
      pendingRead.current = null;
    };
    const fail = (message: string) => {
      if (version !== uploadVersion.current) return;
      finishRead();
      input.setCustomValidity(message);
      setError(message);
      onSaveStatus?.(false);
      emitErrorToast({ icon: "upload", message, buttonLabel: "Dismiss" });
    };
    const save = (write: () => Promise<{ type: string }>) => {
      if (version !== uploadVersion.current) return;
      const writeResult = queueConnectorEdit(app.tenantId, app.id, async () => {
        let saved = false;
        try {
          saved = (await write()).type === "success";
        } catch {
          saved = false;
        }
        if (version !== uploadVersion.current) {
          if (!mounted.current) onSaveStatus?.(saved);
          return;
        }
        if (saved) {
          input.setCustomValidity("");
          setError(null);
          setFileName(file.name);
          onSaveStatus?.(true);
        } else {
          fail(
            "Couldn’t save the file. Re-enter the connector’s secrets and try again."
          );
        }
      });
      finishRead();
      void writeResult;
    };
    if (file.size > MAX_FILE_BYTES) {
      fail(
        `${file.name} is too large. ${item.label} must be under ${MAX_FILE_BYTES / (1024 * 1024)} MB.`
      );
      return;
    }
    input.setCustomValidity("Please wait for the file to finish saving.");
    setError(null);
    const read = Promise.withResolvers<void>();
    pendingRead.current = read;
    void queueConnectorEdit(app.tenantId, app.id, () => read.promise);
    const reader = new FileReader();
    reader.onerror = () =>
      fail("Failed to read the file. Please choose it again.");
    reader.onload = () => {
      void (async () => {
        if (version !== uploadVersion.current) return;
        const text = reader.result as string;
        if (
          target.kind === "configuration" &&
          CA_CERTIFICATE_FIELDS.includes(target.field) &&
          !(await validCaCertificateUpload(text))
        ) {
          fail(INVALID_CA_CERTIFICATE_MESSAGE);
          return;
        }
        const sealed =
          target.kind === "secrets"
            ? await sealSecretInput(
                input,
                text,
                { app, field: target.field },
                () => version === uploadVersion.current
              )
            : undefined;
        if (version !== uploadVersion.current) return;
        if (target.kind === "secrets" && sealed === undefined) {
          fail(input.validationMessage);
          return;
        }
        if (target.kind === "secrets" && sealed !== undefined) {
          save(
            async () =>
              z.mutate(
                sharedMutators.updateConnectedApp(
                  await withConnectorDestinationProof(app, {
                    secrets: { [target.field]: sealed },
                  })
                )
              ).server
          );
        } else if (target.kind === "configuration") {
          const configuration = { ...app.configuration, [target.field]: text };
          confirmGrantRemoval(
            { configuration },
            () =>
              save(
                async () =>
                  z.mutate(
                    sharedMutators.updateConnectedApp(
                      await withConnectorDestinationProof(app, {
                        configurationPatch: { [target.field]: text },
                      })
                    )
                  ).server
              ),
            () => {
              if (version === uploadVersion.current) {
                input.setCustomValidity("");
                finishRead();
                onSaveStatus?.(true);
              }
            }
          );
        } else if (
          target.kind === "oauthConnection" &&
          target.field !== "scopes" &&
          app.oauthConnectionId
        ) {
          save(
            () =>
              z.mutate(
                sharedMutators.updateOAuthConnection({
                  oauthConnectionId: app.oauthConnectionId!,
                  [target.field]: text,
                })
              ).server
          );
        } else if (target.kind === "oauthConnection") {
          fail("Connect this connector before uploading the file.");
        } else {
          input.setCustomValidity("");
          setFileName(file.name);
          finishRead();
          onSaveStatus?.(true);
        }
      })().catch(() =>
        fail("Couldn’t process the file. Please choose it again.")
      );
    };
    reader.readAsText(file);
  };

  const buttonLabel = fileName
    ? fileName
    : hasSaved
      ? "Replace file…"
      : "Choose file…";

  return (
    <div>
      <Box horizontal={8} vertical={10}>
        <Text variant="text-small-md" color="var(--text-primary)">
          {item.label}
        </Text>
      </Box>
      <input
        ref={inputRef}
        type="file"
        accept={item.accept}
        style={{ display: "none" }}
        onInvalid={(e) => e.preventDefault()}
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = "";
          if (file) handleFile(file);
        }}
      />
      <SavedRow>
        <UploadButton
          onPress={() => inputRef.current?.click()}
          disabled={disabled}
        >
          <Icon name="upload" size={12} />
          <Text variant="text-small">{buttonLabel}</Text>
        </UploadButton>
        {hasSaved && !fileName && (
          <Box horizontal={4}>
            <Text variant="text-small" color="var(--text-secondary)">
              On file
            </Text>
          </Box>
        )}
      </SavedRow>
      {(error || saveError) && (
        <div role="alert">
          <Box horizontal={8} top={6}>
            <Text variant="text-small" color="var(--text-error)">
              {error ?? "Couldn’t save the file. Please choose it again."}
            </Text>
            <ButtonPress
              variant="secondary"
              onPress={() => {
                inputRef.current?.setCustomValidity("");
                setError(null);
                onSaveStatus?.(true);
              }}
            >
              Discard upload
            </ButtonPress>
          </Box>
        </div>
      )}
      {item.helperText && (
        <Box horizontal={8} top={6}>
          <Text variant="text-small" color="var(--text-secondary)">
            {item.helperText}
          </Text>
        </Box>
      )}
      {confirmModal}
    </div>
  );
}
